import { useState } from 'react';
import type { MergeHunk } from '../editor/merge';

interface Props {
  hunks: MergeHunk[];
  onApply: (hunks: MergeHunk[]) => void;
  onUseTheirs: () => void;
  onCancel: () => void;
}

function Context({ lines }: { lines: string[] }) {
  const [open, setOpen] = useState(false);
  if (lines.length <= 4 || open) {
    return <pre className="merge-context">{lines.join('\n') || ' '}</pre>;
  }
  return (
    <pre className="merge-context">
      {lines.slice(0, 2).join('\n')}
      {'\n'}
      <button className="merge-expand" onClick={() => setOpen(true)}>
        … {lines.length - 4} unchanged lines …
      </button>
      {'\n'}
      {lines.slice(-2).join('\n')}
    </pre>
  );
}

export default function MergeDialog({ hunks: initial, onApply, onUseTheirs, onCancel }: Props) {
  const [hunks, setHunks] = useState(initial);
  const conflicts = hunks.filter(h => h.kind === 'conflict').length;

  const choose = (index: number, choice: 'mine' | 'theirs' | 'both') =>
    setHunks(hs => hs.map((h, i) => (i === index && h.kind === 'conflict' ? { ...h, choice } : h)));

  let conflictNo = 0;

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="merge-title">
      <div className="modal modal-wide">
        <div className="modal-head">
          <h2 id="merge-title">Someone else changed this page</h2>
          <p className="muted">
            Another editor saved this page while you were working. Changes that don't overlap were merged automatically. Decide what to keep for the {conflicts} overlapping{' '}
            {conflicts === 1 ? 'section' : 'sections'} below, then apply.
          </p>
        </div>
        <div className="modal-body merge-body">
          {hunks.map((h, i) => {
            if (h.kind === 'ok') return <Context key={i} lines={h.lines} />;
            conflictNo += 1;
            return (
              <div className="merge-conflict" key={i}>
                <div className="merge-conflict-head">Conflict {conflictNo}</div>
                <div className="merge-sides">
                  <label className={`merge-side${h.choice === 'mine' ? ' is-chosen' : ''}`}>
                    <input type="radio" name={`c${i}`} checked={h.choice === 'mine'} onChange={() => choose(i, 'mine')} /> Keep mine
                    <pre>{h.mine.join('\n') || '(removed)'}</pre>
                  </label>
                  <label className={`merge-side${h.choice === 'theirs' ? ' is-chosen' : ''}`}>
                    <input type="radio" name={`c${i}`} checked={h.choice === 'theirs'} onChange={() => choose(i, 'theirs')} /> Use theirs
                    <pre>{h.theirs.join('\n') || '(removed)'}</pre>
                  </label>
                </div>
                <label className="merge-both">
                  <input type="radio" name={`c${i}`} checked={h.choice === 'both'} onChange={() => choose(i, 'both')} /> Keep both (mine first)
                </label>
              </div>
            );
          })}
        </div>
        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={onCancel}>
            Keep editing, save later
          </button>
          <button className="btn btn-ghost" onClick={onUseTheirs}>
            Discard my changes
          </button>
          <span className="spacer" />
          <button className="btn btn-primary" onClick={() => onApply(hunks)}>
            Apply merge and save
          </button>
        </div>
      </div>
    </div>
  );
}
